<template>
  <div>
    <DxPopup
      :visible="visible"
      :show-title="true"
      :title="$t('common.products')"
      :wrapper-attr="{ class: 'partner-selector-popup' }"
      :width="'80%'"
      :height="'85%'"
      @hiding="onClose"
    >
      <GridPanelHeader
        :grid-ref="$refs.productGrid"
        :show-add="true"
        :show-refresh="true"
        :show-filter="false"
        :show-export="false"
        :show-columns="false"
        :no-bottom-margin="true"
        @add="showCreateProductDialog = true"
      />
      <DxDataGrid
        ref="productGrid"
        :data-source="productsDataSurce"
        :show-borders="true"
        :column-auto-width="true"
        :remote-operations="true"
        :hover-state-enabled="true"
        :show-column-headers="true"
        :repaint-changes-only="true"
        :selected-row-keys="selectedProductUuids"
        @selection-changed="onGridSelectionChanged($event)"
        @row-click="onRowClick"
        @editor-preparing="onEditorPreparing"
        :element-attr="gridAttributes"
      >
        <DxColumn dataField="name" :caption="$t('common.name')" :filter-operations="['contains']" :allowSorting="false" width="25%" />
        <DxColumn
          dataField="code"
          :caption="$t('stock_management.products.code')"
          :filter-operations="['contains']"
          :allowSorting="false"
          width="15%"
        />
        <DxColumn
          dataField="barcode"
          :caption="$t('stock_management.products.barcode')"
          :filter-operations="['contains']"
          :allowSorting="false"
          width="15%"
        />
        <DxColumn
          dataField="product_type.name"
          :caption="$t('stock_management.products.type')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="measure_unit.name"
          :caption="$t('common.measure_unit')"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
          width="15%"
        />
        <DxColumn
          dataField="is_active"
          cell-template="isActiveViewTemplate"
          :caption="$t('common.active')"
          data-type="number"
          :allow-filtering="true"
          :filter-operations="['=']"
          :allowSorting="false"
          alignment="center"
          width="10%"
          :calculateCellValue="getActiveStatusNameByRow"
        >
          <DxLookup :data-source="activeLookup" value-expr="value" display-expr="text" />
        </DxColumn>
        <template #isActiveViewTemplate="{ data }">
          <span>{{ getActiveStatusName(data?.data?.is_active) }}</span>
        </template>
        <DxColumn
          dataField="lot_management"
          :caption="$t('stock_management.products.lot_management')"
          data-type="number"
          alignment="center"
          :allow-filtering="true"
          :filter-operations="['=']"
          :allowSorting="false"
          width="12%"
          :calculateCellValue="getLotManagementColumnText"
          cell-template="lotManagementCellTemplate"
        >
          <DxLookup :data-source="yes_no_list" value-expr="id" display-expr="label" />
        </DxColumn>
        <template #lotManagementCellTemplate="{ data }">
          <span>{{ getLotManagementColumnText(data.data) }}</span>
        </template>
        <DxColumn
          dataField="updated_at"
          :caption="$t('common.last_update')"
          :filter-operations="['=']"
          :allowSorting="false"
          dataType="date"
          alignment="center"
          width="15%"
          :format="this.getDxGridDefaultDateFormat"
        />
        <DxSelection :mode="multiselect ? 'multiple' : 'single'" showCheckBoxesMode="always" selectAllMode="page" />
        <DxFilterRow :visible="true" />
        <DxGrouping :autoExpandAll="true" />
        <DxGroupPanel :visible="false" />
        <DxPaging :total="3500" :page-size="15" />
        <DxPager
          :visible="true"
          :allowed-page-sizes="pageSizes"
          display-mode="compact"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
          :info-text="$t('common.page_info')"
        />
      </DxDataGrid>
      <div style="display: flex; justify-content: flex-end; gap: 12px; margin-top: auto; padding-top: 16px">
        <SmagriaButton :label="$t('common.continue')" customClass="primary col-lg-2 apply-btn" :action="onContinue" />
      </div>
    </DxPopup>
    <CreateProductDialog
      :visible="showCreateProductDialog"
      v-if="showCreateProductDialog"
      @close="showCreateProductDialog = false"
      @saved="onProductSaved"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  DxDataGrid,
  DxColumn,
  DxSelection,
  DxFilterRow,
  DxGrouping,
  DxGroupPanel,
  DxPaging,
  DxLookup,
  DxPager,
} from "devextreme-vue/data-grid";
import DxPopup from "devextreme-vue/popup";
import CustomStore from "devextreme/data/custom_store";
import SmagriaButton from "@/components/Common/SmagriaButton.vue";
import CreateProductDialog from "~/pages/stock-management/sm-products/partials/CreateProductDialog.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "ProductSelector",
  components: {
    DxDataGrid,
    DxColumn,
    DxPopup,
    DxSelection,
    DxFilterRow,
    DxGrouping,
    DxGroupPanel,
    DxPaging,
    DxLookup,
    DxPager,
    CreateProductDialog,
    GridPanelHeader,
    SmagriaButton,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    multiselect: {
      type: Boolean,
      default: () => true,
    },
    selectedProductUuids: {
      type: Array,
      default: () => [],
    },
    singleProductDepartment: {
      type: Boolean,
      default: () => true,
    },
    filterData: {
      type: Object,
      default: () => ({}),
    },
    lotManagement: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid product_selector_grid",
      },
      productsDataSurce: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = {
            data: [],
            totalCount: 0,
          };

          let pageLimit = loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;

          /*if (isNaN(pageNumber) || !pageNumber) {
            return {
              data: [],
              totalCount: 0,
            };
          }*/

          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
          };

          if (this.filterData && Object.keys(this.filterData).length > 0) {
            Object.assign(payload, this.filterData);
          }

          if (!this.hasIsActiveFilter(payload.filter)) {
            if (!payload.filter) {
              payload.filter = [];
            } else if (payload.filter.length > 0) {
              payload.filter.push("and");
            }
            payload.filter.push(["is_active", "=", 1]);
          }

          try {
            await this.getProducts(payload).then((response) => {
              dataSource.data = response.data;
              dataSource.totalCount = response?.pagination?.total ?? 1;
            });

            return dataSource;
          } catch (error) {
            console.error("Error fetching products:", error);
            throw error;
          }
        },
        remove: this.removeWeighingOnScale,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 25, 100],
      localSelectedProductUuids: [...this.selectedProductUuids],
      showCreateProductDialog: false,
      activeLookup: [
        { value: 1, text: this.$t("common.active") },
        { value: 0, text: this.$t("common.inactive") },
      ],
      yes_no_list: [
        { id: 1, label: this.$t("common.yes") },
        { id: 0, label: this.$t("common.no") },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
  },
  watch: {
    selectedProductUuids: {
      deep: true,
      handler(newData) {
        this.localSelectedProductUuids = [...newData];
        this.$refs.productGrid.instance.selectRows(newData, false);
      },
    },
  },
  methods: {
    ...mapActions({
      getProducts: "smProducts/getSmProducts",
    }),
    onClose() {
      this.$emit("close");
    },
    onProductSaved(product) {
      const createdProduct = product?.data || product;
      this.$nextTick(() => {
        this.$refs.productGrid?.instance.refresh().then(() => {
          if (createdProduct?.uuid) {
            this.$refs.productGrid.instance.selectRows([createdProduct.uuid], false);
          }
        });
      });
    },
    onContinue() {
      const selectedRows = this.$refs.productGrid.instance.getSelectedRowsData();
      this.$emit("continue", selectedRows ?? []); // Emit the selected products to the parent component

      this.$refs.productGrid.instance.clearSelection();
      this.localSelectedProductUuids = [];
      this.$emit("update:selectedProductUuids", []);
    },
    onGridSelectionChanged(e) {
      /*if (!this.checkDepartmentRestrictionForSelection(e)) {
        return;
      }*/
      //this.$emit("update:selectedProducts", e.selectedRowKeys);
    },
    onRowClick(e) {
      // Prevent default behavior if the row is already selected and multiselect is false
      if (!this.multiselect) {
        return;
      }

      /*if (!this.checkDepartmentRestrictionForSelection(e)) {
        return;
      }*/

      const rowKey = e.key; // Get the unique key of the clicked row
      const selectedIndex = this.selectedProductUuids.indexOf(rowKey);

      if (selectedIndex === -1) {
        // If the row is not selected, add it to the selection
        this.selectedProductUuids.push(rowKey);
      } else {
        // If the row is already selected, remove it from the selection
        this.selectedProductUuids.splice(selectedIndex, 1);
      }

      // Update the grid's selected rows
      this.$refs.productGrid.instance.selectRows(this.selectedProductUuids, false);
    },
    hasIsActiveFilter(filter) {
      if (!Array.isArray(filter)) {
        return false;
      }
      return filter.some((item) => {
        if (Array.isArray(item)) {
          return item[0] === "is_active";
        }
        return item === "is_active";
      });
    },
    getActiveStatusName(value) {
      return this.activeLookup.find((item) => item.value == value)?.text ?? "";
    },
    getActiveStatusNameByRow(rowData) {
      return this.getActiveStatusName(rowData?.is_active);
    },
    getLotManagementColumnText(rowData) {
      return this.yes_no_list.find((item) => item.id == rowData?.lot_management)?.label ?? "";
    },
    onEditorPreparing(e) {
      if (e.parentType === "filterRow" && e.dataField === "lot_management" && this.lotManagement !== null) {
        e.editorOptions.disabled = true;
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.productGrid && this.$refs.productGrid.instance) {
        this.$refs.productGrid.instance.columnOption("is_active", "filterValue", 1);
        if (this.lotManagement !== null) {
          this.$refs.productGrid.instance.columnOption("lot_management", "filterValue", this.lotManagement ? 1 : 0);
        }
      }
    });
  },
};
</script>

<style lang="scss">
.product-selector-popup .dx-popup-content {
  padding-top: 0 !important;
}
</style>

<style lang="scss" scoped>
.product_selector_grid {
  height: calc(95vh - 290px) !important;
}
</style>
